import React, { useEffect, useRef } from 'react';
import * as dc from 'dc';
import * as d3 from 'd3';
import $ from 'jquery';
import D3ChartService from './d3ChartService';

interface BarChartProps {
  reportData: {
    series: any[];
    categories: string[];
  };
  toolTipLabelFunction?: (stackName: string, value: any, altValue: any, category?: string) => string;
  height: number;
  minWidth?: number;
  bottomMargin?: number;
  leftMargin?: number;
  highlightOnHover?: boolean;
  centerSingleBar?: boolean;
  yAxisNoThousandsSeparator?: boolean;
  disableClickFilter?: boolean;
  tooltipPosition?: 'top' | 'right';
  /** Hide (instead of wrap) x-axis labels that don't fit their tick width. */
  hideOverflowingAxisLabels?: boolean;
  /** Keep x-axis labels on a single line instead of wrapping onto multiple lines. */
  singleLineAxisLabels?: boolean;
  /** Minimum rendered bar height (px) so tiny/zero values stay hoverable. */
  minBarHeight?: number;
}

const BarChart: React.FC<BarChartProps> = ({
  reportData,
  toolTipLabelFunction,
  height,
  minWidth,
  bottomMargin,
  leftMargin,
  highlightOnHover = false,
  centerSingleBar = false,
  yAxisNoThousandsSeparator = false,
  disableClickFilter = false,
  tooltipPosition = 'top',
  hideOverflowingAxisLabels = false,
  singleLineAxisLabels = false,
  minBarHeight,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const {
    buildChartData,
    watchResizeEvents,
    resizeChart,
    adjustBar,
    adjustBarLabels,
    adjustLegend,
    addTooltips,
  } = D3ChartService;

  useEffect(() => {
    if (reportData && chartRef.current) {
      const chartData = buildChartData(reportData.series, reportData.categories);
      const barChart = dc.barChart(chartRef.current as any);

      if (minWidth) {
        $(chartRef.current).css('min-width', `${minWidth}px`);
      }

      if (bottomMargin) {
        barChart.margins().bottom = bottomMargin;
      }
      barChart.margins().top = 30;
      barChart.margins().left = leftMargin || 50;

      barChart
        .height(height)
        .dimension(chartData.dimension)
        .group(chartData.group, chartData.stackNames[0])
        .valueAccessor(d => d.value.values[0])
        .x(d3.scale.ordinal().domain(reportData.categories))
        .xUnits(dc.units.ordinal)
        .ordering(d => reportData.categories.indexOf(d.key))
        .y(d3.scale.linear().domain([0, 1.1 * chartData.maxValue]))
        .renderHorizontalGridLines(true)
        .elasticX(true)
        .centerBar(true)
        .gap(10);


      if (disableClickFilter) {
        barChart.brushOn(false);
        barChart.onClick = () => undefined;
      }

      barChart.xAxis().tickSize(2);
      barChart
        .yAxis()
        .tickSize(2)
        .ticks(5);
      if (yAxisNoThousandsSeparator) {
        barChart.yAxis().tickFormat((d: any) => String(d));
      }
      barChart.legend(
        dc
          .legend()
          .x(5)
          .y(barChart.height() - (chartData.stackSize*22.5)+40),
      );

      // stacked data - the first one is from the "valueAccessor" above
      for (let i = 1; i < chartData.stackSize; i++) {
        barChart.stack(
          chartData.group,
          chartData.stackNames[i],
          d => d.value.values[i],
        );
      }

      // tooltips
      const toolTipLabel = toolTipLabelFunction || ((stackName, value, altValue, category) => `${stackName}: ${altValue}`);
      for (let i = 0; i < chartData.stackSize; i++) {
        barChart.title(chartData.stackNames[i], d =>
          toolTipLabel(
            chartData.stackNames[i],
            d.value.values[i],
            d.value.altValues[i],
            d.key,
          ),
        );
      }

      const resizeWatch = watchResizeEvents(barChart, chartRef.current, resizeChart);

      // Set transition duration to 0 before rendering
      barChart.transitionDuration(0);

      barChart.on('renderlet.adjustBar', (c: any) => {
        D3ChartService.adjustBar(c);
        if (minBarHeight) {
          D3ChartService.enforceMinBarHeight(c, minBarHeight);
        }
      });
      barChart.on('renderlet.adjustBarLabels', (c: any) => {
        if (hideOverflowingAxisLabels) {
          D3ChartService.adjustBarLabelsHideOverflow(c);
        } else if (singleLineAxisLabels) {
          D3ChartService.adjustBarLabelsSingleLine(c);
        } else {
          D3ChartService.adjustBarLabels(c);
        }
      });
      barChart.on('renderlet.adjustLegend', adjustLegend);
      barChart.on('renderlet.addTooltips', (c: any) => {
        D3ChartService.addTooltips(c, tooltipPosition === 'right' ? 'e' : 'n');
      });

      // Grey-out other bars on hover: dim all bars, highlight the hovered one.
      if (highlightOnHover) {
        barChart.on('renderlet', (c: any) => {
          const bars = c.selectAll('.bar');
          bars
            .on('mouseover.greyout', function (this: any) {
              bars.style('opacity', 0.25);
              d3.select(this).style('opacity', 1);
            })
            .on('mouseout.greyout', function () {
              bars.style('opacity', 1);
            });
        });
      }

      // When the chart has a single category, dc's ordinal scale stretches the
      // lone bar across the whole plot area. Give it a normal fixed width and
      // center it (along with its x-axis label) instead.
      // Namespaced so it runs in addition to the other 'renderlet' listeners
      // and only acts when there is exactly one bar. Opt-in via centerSingleBar
      // so only the self-service usage charts are affected.
      if (centerSingleBar) {
        barChart.on('renderlet.center-single-bar', (c: any) => {
          if (c.xUnitCount() !== 1) {
            return;
          }
          const SINGLE_BAR_WIDTH = 50;
          const axisLength = c.xAxisLength();
          const barX = (axisLength - SINGLE_BAR_WIDTH) / 2;
          c.selectAll('.bar')
            .attr('width', SINGLE_BAR_WIDTH)
            .attr('x', dc.utils.safeNumber(barX));
          c.select('.axis.x')
            .selectAll('.tick')
            .attr('transform', `translate(${dc.utils.safeNumber(axisLength / 2)},0)`);
        });
      }

      barChart.render();

      // Restore transition duration to 750 after rendering
      barChart.transitionDuration(750);

      return () => {
        if (resizeWatch) {
          resizeWatch();
        }
      };
    }
  }, [reportData, toolTipLabelFunction, height, minWidth, bottomMargin, leftMargin, highlightOnHover, centerSingleBar, yAxisNoThousandsSeparator, disableClickFilter, tooltipPosition, hideOverflowingAxisLabels, singleLineAxisLabels, minBarHeight, buildChartData, watchResizeEvents, resizeChart, adjustBar, adjustBarLabels, adjustLegend, addTooltips]);

  return <div ref={chartRef} id="bar-chart"></div>;
};

export default BarChart;